import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Plus, Search, Package, Pencil } from 'lucide-react';
import { api } from '@/lib/api';
import { fmt } from '@/lib/format';
import { Card, CardBody } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { ArticuloFormModal, type ArticuloInitial } from './ArticuloFormModal';

type Articulo = {
  id: string;
  clave: string;
  descripcion: string;
  categoria_id?: string;
  categoria_nombre?: string;
  unidad_medida_id?: string;
  unidad_clave?: string;
  marca?: string;
  modelo?: string;
  partida_cog_id?: string;
  partida_cog_codigo?: string;
  costo_promedio: number;
  minimo: number;
  maximo: number;
  punto_reorden: number;
  controla_lote: boolean;
  activo: boolean;
};

export function ArticulosPage() {
  const [q, setQ] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<{ id: string; initial: ArticuloInitial } | null>(null);

  const { data, refetch } = useQuery({
    queryKey: ['articulos', q],
    queryFn: () => {
      const p = new URLSearchParams();
      if (q) p.set('q', q);
      p.set('limit', '200');
      return api<{ total: number; items: Articulo[] }>(`/articulos?${p}`);
    },
  });

  const items = data?.items ?? [];

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-[12px] font-semibold uppercase tracking-[0.14em] text-ink-mute">Recursos Materiales · Padrón de artículos</p>
          <h1 className="mt-1 font-display text-[28px] font-bold tracking-tight text-ink">Artículos</h1>
          <p className="mt-1 text-[14px] text-ink-mute">
            SKU propio + clave SAT (ProdServ CFDI), partida COG obligatoria. Control de lote/serie cuando aplica.
          </p>
        </div>
        <Button onClick={() => setFormOpen(true)}>
          <Plus size={16} /> Nuevo artículo
        </Button>
      </div>

      <Input
        name="q"
        placeholder="Buscar por clave, descripción o código de barras…"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        leading={<Search size={16} />}
      />

      <Card>
        <CardBody className="p-0">
          {items.length === 0 ? (
            <div className="flex flex-col items-center gap-3 p-10 text-center">
              <Package size={28} className="text-ink-soft" />
              <p className="text-[13px] text-ink-mute">
                {q ? 'Sin coincidencias.' : 'Aún no hay artículos. Crea el primero.'}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-[13px]">
                <thead className="bg-muted/60 text-left text-[11px] font-semibold uppercase tracking-[0.08em] text-ink-mute">
                  <tr>
                    <th className="px-5 py-3">Clave</th>
                    <th className="px-5 py-3">Descripción</th>
                    <th className="px-5 py-3">Unidad</th>
                    <th className="px-5 py-3">Categoría</th>
                    <th className="px-5 py-3">COG</th>
                    <th className="px-5 py-3 text-right">Costo prom.</th>
                    <th className="px-5 py-3 text-right">Mín.</th>
                    <th className="px-5 py-3 text-right">Reorden</th>
                    <th className="px-5 py-3"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {items.map((r) => (
                    <tr key={r.id} className="transition-colors hover:bg-muted/50">
                      <td className="num px-5 py-3 font-semibold text-brand-700 dark:text-brand-300">{r.clave}</td>
                      <td className="px-5 py-3">
                        <div className="text-ink">{r.descripcion}</div>
                        {(r.marca || r.modelo) && (
                          <div className="text-[11px] text-ink-mute">
                            {[r.marca, r.modelo].filter(Boolean).join(' · ')}
                          </div>
                        )}
                      </td>
                      <td className="num px-5 py-3 text-ink-mute">{r.unidad_clave ?? '—'}</td>
                      <td className="px-5 py-3 text-ink-mute">{r.categoria_nombre ?? '—'}</td>
                      <td className="num px-5 py-3">{r.partida_cog_codigo ?? '—'}</td>
                      <td className="num px-5 py-3 text-right">{fmt.money(r.costo_promedio)}</td>
                      <td className="num px-5 py-3 text-right">{fmt.num(r.minimo)}</td>
                      <td className="num px-5 py-3 text-right">{fmt.num(r.punto_reorden)}</td>
                      <td className="px-5 py-3 text-right">
                        <div className="inline-flex items-center gap-1">
                          <button
                            onClick={() => setEditing({
                              id: r.id,
                              initial: {
                                clave: r.clave,
                                descripcion: r.descripcion,
                                unidad_medida_id: r.unidad_medida_id,
                                categoria_id: r.categoria_id,
                                partida_cog_id: r.partida_cog_id,
                                marca: r.marca,
                                modelo: r.modelo,
                                minimo: r.minimo,
                                maximo: r.maximo,
                                punto_reorden: r.punto_reorden,
                                controla_lote: r.controla_lote,
                              },
                            })}
                            className="rounded-md p-1.5 text-ink-mute hover:bg-muted hover:text-ink"
                            title="Editar"
                          >
                            <Pencil size={14} />
                          </button>
                          <Link to={`/app/articulos/${r.id}`}>
                            <Button variant="ghost" size="sm">Ver</Button>
                          </Link>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="px-5 py-3 border-t border-border text-[12px] text-ink-mute">
                {items.length} de {data?.total ?? 0}
              </div>
            </div>
          )}
        </CardBody>
      </Card>

      <ArticuloFormModal
        open={formOpen}
        onClose={() => setFormOpen(false)}
        onCreated={() => { setFormOpen(false); refetch(); }}
      />
      <ArticuloFormModal
        open={editing !== null}
        id={editing?.id}
        initial={editing?.initial}
        onClose={() => setEditing(null)}
        onCreated={() => { setEditing(null); refetch(); }}
      />
    </div>
  );
}
